/**
 * Shared result-summarization utilities for the agent flow.
 *
 * Lives in its own module (not main-agent.ts) so BOTH the main agent and the
 * source agent can summarize a full result set without a circular import.
 */

import { MAX_ROWS_TO_LLM } from '../constants';

/**
 * Largest result the main agent is shown VERBATIM (every row). At or below this
 * the rows are sent complete — so small dimension/lookup results (benchmark
 * mappings, enum lists) arrive whole and the agent never generalizes from a
 * biased head-slice. Above it, the agent gets a bounded `summarizeRows` summary
 * (complete STRUCTURE, not rows) + a few samples instead.
 * Alias of the canonical STAGE-2 limit (see constants.ts).
 */
export const MAIN_AGENT_COMPLETE_ROWS = MAX_ROWS_TO_LLM;

/**
 * Profile a full result set into a compact, LLM-readable summary.
 * Computed over ALL rows so the agent can state ranges, counts, and
 * distributions truthfully even though it is only shown a few sample rows.
 * Source-agnostic — infers number / date / category / text per column.
 *
 * Output size is BOUNDED regardless of input size (categories and groups are
 * capped), so a 1M-row result and a 1K-row result yield a same-sized summary.
 *
 * Beyond per-column stats, it emits LABEL-carrying facts so the agent can name
 * extremes/rankings/trends without guessing which row holds a value:
 *  - `extremes`  — per measure, the argmin/argmax ROW (value + its label columns)
 *  - `topK`/`bottomK` (+ `rankedBy`) — exact ranking rows by the primary measure
 *  - `ordering`  — for a date/period dimension: first/last/peak/trough/deltaPct/
 *                  direction, and `lastPeriodPartial` when the trailing period
 *                  looks incomplete (count is a strong low outlier).
 */
export function summarizeRows(rows: Record<string, any>[]): Record<string, any> {
	if (!rows.length) return { totalRows: 0, columns: {} };
	const MAX_CATEGORIES = 20;
	const keys = Object.keys(rows[0]);
	const columns: Record<string, any> = {};
	// Local-time date formatter — avoids the UTC shift that toISOString() applies
	// to a locally-parsed "YYYY-MM-DD HH:mm:ss" string (which would roll the date
	// back a day in positive-offset timezones).
	const fmtDate = (ms: number) => {
		const d = new Date(ms);
		const p = (n: number) => String(n).padStart(2, '0');
		return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
	};

	for (const key of keys) {
		let nonNull = 0, zeros = 0, isNumeric = true, isDateLike = true, sum = 0;
		let min: number | undefined, max: number | undefined;
		let dMin: number | undefined, dMax: number | undefined;
		const counts = new Map<string, number>();
		let overflow = false;

		for (const row of rows) {
			const v = row[key];
			if (v === null || v === undefined || v === '') continue;
			nonNull++;

			const num = typeof v === 'number' ? v
				: (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : NaN);
			if (!Number.isNaN(num) && typeof v !== 'boolean') {
				if (num === 0) zeros++;
				min = min === undefined ? num : Math.min(min, num);
				max = max === undefined ? num : Math.max(max, num);
				sum += num;
			} else { isNumeric = false; }

			const t = v instanceof Date ? v.getTime()
				: (typeof v === 'string' ? Date.parse(v) : NaN);
			if (!Number.isNaN(t) && typeof v !== 'number') {
				dMin = dMin === undefined ? t : Math.min(dMin, t);
				dMax = dMax === undefined ? t : Math.max(dMax, t);
			} else { isDateLike = false; }

			if (!overflow) {
				const sv = String(v);
				counts.set(sv, (counts.get(sv) || 0) + 1);
				if (counts.size > MAX_CATEGORIES * 5) overflow = true;
			}
		}

		const nulls = rows.length - nonNull;
		if (nonNull === 0) { columns[key] = { type: 'empty', nulls }; continue; }

		if (isNumeric) {
			columns[key] = { type: 'number', nonNull, nulls, zeros, min, max,
				avg: Math.round((sum / nonNull) * 100) / 100 };
		} else if (isDateLike) {
			columns[key] = { type: 'date', nonNull,
				min: fmtDate(dMin!),
				max: fmtDate(dMax!),
				distinct: overflow ? `${MAX_CATEGORIES * 5}+` : counts.size };
		} else if (!overflow && counts.size <= MAX_CATEGORIES) {
			const dist: Record<string, number> = {};
			for (const [k, c] of [...counts.entries()].sort((a, b) => b[1] - a[1])) dist[k] = c;
			columns[key] = { type: 'category', distinct: counts.size, counts: dist };
		} else {
			columns[key] = { type: 'text',
				distinct: overflow ? `${MAX_CATEGORIES * 5}+` : counts.size,
				examples: [...counts.keys()].slice(0, 3) };
		}
	}

	// Group-aware measure stats: per-segment averages so the agent can state
	// per-group numbers truthfully (the global column stats above are NOT enough
	// for "JSW vs benchmark per district" — it would otherwise guess from the
	// head sample). Group by the low-cardinality category columns; report the
	// mean of each numeric measure per group.
	const dims = keys
		.filter(k => columns[k]?.type === 'category' && (columns[k].distinct as number) <= 6)
		.sort((a, b) => (columns[a].distinct as number) - (columns[b].distinct as number));
	const measures = keys.filter(k => columns[k]?.type === 'number');
	let groups: Record<string, any> | undefined;
	if (dims.length && measures.length) {
		const MAX_GROUPS = 30;
		let useDims = dims.slice(0, 2);
		const keyOf = (row: Record<string, any>) => useDims.map(d => `${d}=${row[d]}`).join(' | ');
		if (new Set(rows.map(keyOf)).size > MAX_GROUPS) useDims = dims.slice(0, 1); // fall back to 1 dim
		const agg = new Map<string, { count: number; sums: Record<string, number>; ns: Record<string, number> }>();
		for (const row of rows) {
			const key = useDims.map(d => `${d}=${row[d]}`).join(' | ');
			let g = agg.get(key);
			if (!g) { g = { count: 0, sums: {}, ns: {} }; agg.set(key, g); }
			g.count++;
			for (const m of measures) {
				const v = row[m];
				if (typeof v === 'number' && !Number.isNaN(v)) {
					g.sums[m] = (g.sums[m] || 0) + v;
					g.ns[m] = (g.ns[m] || 0) + 1;
				}
			}
		}
		if (agg.size <= MAX_GROUPS) {
			// Count-like measures (counts/totals) need SUM per group; rate/price
			// measures need AVG. Averaging a count per row understates the true
			// total (e.g. evidential_count avg 0.7/day reads as "~0" when the
			// district actually has dozens of evidential observations).
			const isCountLike = (name: string) => /count|\bobs\b|observation|qty|quantity|\bnum|total|evidential|volume|units/i.test(name);
			groups = {
				by: useDims,
				rows: [...agg.entries()].map(([key, g]) => {
					const avg: Record<string, number> = {};
					const sum: Record<string, number> = {};
					for (const m of measures) {
						if (!g.ns[m]) continue;
						avg[m] = Math.round((g.sums[m] / g.ns[m]) * 100) / 100;
						if (isCountLike(m)) sum[m] = Math.round(g.sums[m] * 100) / 100;
					}
					return Object.keys(sum).length ? { key, count: g.count, avg, sum } : { key, count: g.count, avg };
				}),
			};
		}
	}

	// ── Label-carrying facts so the agent can NAME extremes / rankings / trends
	// truthfully instead of guessing which row holds a min/max. The plain column
	// stats above give a bare min/max VALUE with no idea which row it belongs to,
	// which is what makes the agent attach the right number to the wrong label
	// ("peak in June" when June is actually the trough). Everything here is
	// bounded (≤6 measures, K=5, one sequence dim) so size stays O(1) in N. ──
	const compact = (row: Record<string, any>) => {
		const o: Record<string, any> = {};
		for (const k of keys) {
			const v = row[k];
			o[k] = (typeof v === 'string' && v.length > 80) ? v.slice(0, 80) + '…' : v;
		}
		return o;
	};
	const asNum = (v: any) =>
		typeof v === 'number' ? v
			: (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : NaN);

	// Per-measure argmin/argmax — the actual ROW (with its labels) at each extreme.
	const extremes: Record<string, any> = {};
	for (const m of measures.slice(0, 6)) {
		let minRow: Record<string, any> | undefined, maxRow: Record<string, any> | undefined;
		let minV = Infinity, maxV = -Infinity;
		for (const row of rows) {
			const n = asNum(row[m]);
			if (Number.isNaN(n)) continue;
			if (n < minV) { minV = n; minRow = row; }
			if (n > maxV) { maxV = n; maxRow = row; }
		}
		if (minRow && maxRow) {
			extremes[m] = { min: { value: minV, row: compact(minRow) }, max: { value: maxV, row: compact(maxRow) } };
		}
	}

	// Top-K / bottom-K by the primary (first) measure — exact ranking over ALL rows,
	// so "top N …" is answered from data, never inferred from a head slice.
	let rankedBy: string | undefined, topK: Record<string, any>[] | undefined, bottomK: Record<string, any>[] | undefined;
	if (measures.length) {
		const primary = measures[0];
		const ranked = rows
			.map(r => ({ r, n: asNum(r[primary]) }))
			.filter(x => !Number.isNaN(x.n))
			.sort((a, b) => b.n - a.n);
		if (ranked.length) {
			const K = Math.min(5, ranked.length);
			rankedBy = primary;
			topK = ranked.slice(0, K).map(x => compact(x.r));
			if (ranked.length > K) bottomK = ranked.slice(-K).reverse().map(x => compact(x.r));
		}
	}

	// Trend facts when a sequence dimension (a date column, or yyyy / yyyy-MM period
	// text) exists — first/last/peak/trough/delta so "trending over time" is grounded.
	let ordering: Record<string, any> | undefined;
	const isPeriod = (s: any) =>
		/^\d{4}([-/]\d{2}([-/]\d{2})?)?$/.test(String(s)) || /^\d{4}[-/ ](Q[1-4]|\d{1,2})$/i.test(String(s));
	let seqKey = keys.find(k => columns[k]?.type === 'date');
	if (!seqKey) seqKey = keys.find(k =>
		(columns[k]?.type === 'category' || columns[k]?.type === 'text') &&
		rows.some(r => r[k] != null) &&
		rows.every(r => r[k] == null || isPeriod(r[k])));
	if (seqKey && measures.length) {
		const primary = measures[0];
		const seq = rows
			.filter(r => r[seqKey!] != null && !Number.isNaN(asNum(r[primary])))
			.map(r => ({ k: String(r[seqKey!]), n: asNum(r[primary]), r }))
			.sort((a, b) => a.k < b.k ? -1 : a.k > b.k ? 1 : 0);  // lexical sort is chronological for yyyy-MM(-dd)
		if (seq.length >= 2) {
			const first = seq[0], last = seq[seq.length - 1];
			const peak = seq.reduce((mx, x) => x.n > mx.n ? x : mx, seq[0]);
			const trough = seq.reduce((mn, x) => x.n < mn.n ? x : mn, seq[0]);
			const deltaPct = first.n !== 0 ? Math.round(((last.n - first.n) / Math.abs(first.n)) * 1000) / 10 : null;
			ordering = {
				by: seqKey, measure: primary,
				first: { [seqKey!]: first.k, [primary]: first.n },
				last: { [seqKey!]: last.k, [primary]: last.n },
				peak: { [seqKey!]: peak.k, [primary]: peak.n },
				trough: { [seqKey!]: trough.k, [primary]: trough.n },
				deltaPct,
				direction: deltaPct == null ? 'flat' : deltaPct > 2 ? 'up' : deltaPct < -2 ? 'down' : 'flat',
			};
			// Flag a partial trailing period: a count-like measure whose LAST value is a
			// strong low outlier vs the prior periods (e.g. the current, incomplete month).
			const countKey = measures.find(k => /count|\bobs\b|qty|quantity|\bnum|volume|units|records?/i.test(k));
			if (countKey) {
				const cs = seq.map(x => asNum(x.r[countKey])).filter(n => !Number.isNaN(n));
				if (cs.length >= 4) {
					const prior = cs.slice(0, -1).slice().sort((a, b) => a - b);
					const med = prior[Math.floor(prior.length / 2)];
					if (med > 0 && cs[cs.length - 1] < 0.4 * med) ordering.lastPeriodPartial = true;
				}
			}
		}
	}

	const facts: Record<string, any> = {};
	if (Object.keys(extremes).length) facts.extremes = extremes;
	if (topK) { facts.rankedBy = rankedBy; facts.topK = topK; if (bottomK) facts.bottomK = bottomK; }
	if (ordering) facts.ordering = ordering;

	return groups
		? { totalRows: rows.length, columns, groups, ...facts }
		: { totalRows: rows.length, columns, ...facts };
}
